<template>
  <div class="tw-benchmark-cell">
    <el-tag
      v-if="columnKey === 'campaign_type'"
      size="mini"
      disable-transitions
      :class="isAteluSupportedFormat(row.post.campaignType) ? 'format-tag--primary' : 'format-tag--gray'"
    >
      <!-- 種別と（詳細）を別要素（block）に分け、white-space に依存せず改行する -->
      <span class="tw-benchmark-cell__format-type">{{ campaignTypeParts.type }}</span>
      <span v-if="campaignTypeParts.detail" class="tw-benchmark-cell__format-detail">
        {{ campaignTypeParts.detail }}
      </span>
    </el-tag>
    <span v-else-if="columnKey === 'incentive'">{{ displayValue }}</span>
    <template v-else-if="columnKey === 'is_trend'">
      <span v-if="row.post.isTrend" class="tw-benchmark-cell--trend">あり</span>
      <span v-else class="tw-benchmark-cell--not-trend">-</span>
    </template>
    <el-tooltip
      v-else-if="columnKey === 'campaign_detail' && row.post.campaignDetail"
      :content="displayValue"
      placement="top"
      :open-delay="300"
    >
      <div class="tw-benchmark-cell--detail">{{ displayValue }}</div>
    </el-tooltip>
    <el-tooltip
      v-else-if="columnKey === 'campaign_period' && !hasCampaignEnd"
      content="このキャンペーンは終了日時が収集できませんでした"
      placement="top"
    >
      <!-- 開始日時・終了日時を別要素（block）に分け、キャンペーン一覧画面と同様に改行表示する -->
      <div>
        <span class="tw-benchmark-cell__period-line">{{ campaignPeriodParts.start }} -</span>
        <span class="tw-benchmark-cell__period-line">{{ campaignPeriodParts.end }}</span>
      </div>
    </el-tooltip>
    <template v-else-if="columnKey === 'campaign_period'">
      <!-- 開始日時・終了日時を別要素（block）に分け、キャンペーン一覧画面と同様に改行表示する -->
      <span class="tw-benchmark-cell__period-line">{{ campaignPeriodParts.start }} -</span>
      <span class="tw-benchmark-cell__period-line">{{ campaignPeriodParts.end }}</span>
    </template>
    <el-tooltip
      v-else-if="columnKey === 'campaign_days' && !hasCampaignEnd"
      content="キャンペーン終了日が収集できなかったため表示できません"
      placement="top"
    >
      <span>{{ displayValue }}</span>
    </el-tooltip>
    <span v-else>{{ displayValue }}</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import { type ColumnKey, type PostListResponse } from '@/typings/twitter_benchmark_campaign_posts'

import { formatIncentive, isAteluSupportedFormat, splitCampaignTypeLabel } from '../../../utils/campaignLabels'
import {
  formatDateTimePadded,
  formatDateTimeShort,
  formatNumber,
  formatPercent,
  formatWinnerCount
} from '../../../utils/formatters'

type PostRow = PostListResponse['posts'][number]

/**
 * 一覧テーブルの本体セル 1 つ分を、列キーに応じて post / metrics のフィールドを整形して描画する。
 */
export default defineComponent({
  name: 'TwBenchmarkTableBody',

  props: {
    columnKey: {
      type: String as PropType<ColumnKey>,
      required: true
    },
    row: {
      type: Object as PropType<PostRow>,
      required: true
    }
  },

  setup(props) {
    const displayValue = computed<string>(() => {
      const { post, metrics } = props.row
      switch (props.columnKey) {
        case 'campaign_type':
          return ''
        case 'incentive':
          return formatIncentive(post.incentive)
        case 'winner_count':
          return formatWinnerCount(post.winnerCount)
        case 'is_trend':
          return ''
        case 'campaign_detail':
          return post.campaignDetail || '-'
        case 'impressions_count':
          return formatNumber(metrics.impressionsCount)
        case 'likes_count':
          return formatNumber(metrics.likesCount)
        case 'retweets_count':
          return formatNumber(metrics.retweetsCount)
        case 'quotes_count':
          return formatNumber(metrics.quotesCount)
        case 'replies_count':
          return formatNumber(metrics.repliesCount)
        case 'reactions_count':
          return formatNumber(metrics.reactionsCount)
        case 'reactions_rate':
          return formatPercent(metrics.reactionsRate)
        case 'campaign_period':
          return ''
        case 'campaign_days':
          return formatNumber(post.campaignDays)
        case 'last_synced_at':
          return formatDateTimeShort(post.lastSyncedAt)
        default:
          return '-'
      }
    })

    const campaignTypeParts = computed(() => splitCampaignTypeLabel(props.row.post.campaignType))

    const hasCampaignEnd = computed<boolean>(() => props.row.post.campaignEndDatetime != null)

    const campaignPeriodParts = computed(() => ({
      start: formatDateTimePadded(props.row.post.campaignStartDatetime),
      end: formatDateTimePadded(props.row.post.campaignEndDatetime)
    }))

    return { displayValue, campaignTypeParts, hasCampaignEnd, campaignPeriodParts, isAteluSupportedFormat }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.tw-benchmark-cell {
  text-align: left;

  &--trend {
    color: $text-color;
  }

  &--not-trend {
    color: $sub-text-color;
  }

  &--detail {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 5;
    font-size: 12px;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    cursor: default;
  }
}

.tw-benchmark-cell :deep(.el-tag) {
  height: auto;
  padding-top: 2px;
  padding-bottom: 2px;
  line-height: 1.4;
}

.tw-benchmark-cell__format-type,
.tw-benchmark-cell__format-detail {
  display: block;
}

.tw-benchmark-cell__period-line {
  display: block;
  white-space: nowrap;
}

.format-tag--primary {
  color: $primary-color;
  background-color: rgba($primary-color, 0.12);
  border-color: $primary-color;
}

.format-tag--gray {
  color: $sub-text-color;
  background-color: #f4f4f5;
  border-color: #e9e9eb;
}
</style>
